import { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import InviteCodeForm from '../forms/InviteCodeForm';
import { resolveInviteCompany, removeInviteError } from '../../actions/user';
import { keycloakRegistrationFlagsSelector } from '../../helpers/selectors';
import { CHOOSER_STATE, getChooserState } from '../../helpers/ssoRegistrationFlow';
import { navigateToCreationPage } from '../../utils/utils';

// Which headline goes where, per state. With choices to offer, the screen keeps its
// "which community" question and the invite box introduces itself as one more way
// in; with nothing but the invite box, that box's own intro is the whole point of
// the screen, so it is promoted to the headline - and the box then drops its copy
// of it (titleId null) instead of printing the same sentence twice. With nothing to
// offer at all there is no headline: asking which community to join over a message
// saying none can be joined is a question the screen cannot honour.
const TITLE_ID = {
  CHOOSE_COMPANY: 'only.sso.select.company.select.title',
  INVITE_BESIDE_CHOICES: 'service.corporate.intro.optional',
  INVITE_ALONE: 'service.corporate.intro'
};

// The headline look, owned here rather than handed in by each screen: as a prop it
// was two hand-written class strings that had already drifted apart (bold 16.8px on
// the picker against 25px on the flags screen). The screen-headline idiom the rest
// of the app uses (FaqPopup, MobileAppSelectorPopup, VrInfoPopup).
const TITLE_CLASS = 'text-center f-25 mt-40 mb-10';

/**
 * The single mobile-style "pick your company" screen body (PublicCompaniesActivity
 * mirror), shared by the flags screen and the company picker: the list of choices,
 * the invitation code beside it when the brand allows it, and an honest
 * "registration unavailable" message when there is nothing to offer at all.
 *
 * The wrappers pass the list (`items`, null while it is still loading) and how to
 * render it (`renderList`); the states, their layout and the invite handling live
 * here so both screens behave identically - and so does the look: the headline and
 * the content column (.chooser-column) are this component's, not the wrappers'.
 */
class CompanyChooser extends Component {
  constructor(props) {
    super(props);
    this.handleInviteSubmit = this.handleInviteSubmit.bind(this);
  }

  handleInviteSubmit(data) {
    this.props.resolveInviteCompany(data.inviteCode).then((companyId) => {
      if (companyId) navigateToCreationPage(companyId);
    });
  }

  renderTitle(messageId) {
    return (
      <div className={TITLE_CLASS}>
        <FormattedMessage id={messageId} />
      </div>
    );
  }

  // `titleId` null leaves the box headless: the screen already carries that text.
  renderInviteForm(titleId) {
    return (
      <InviteCodeForm
        titleId={titleId}
        onSubmit={this.handleInviteSubmit}
        onRetry={this.props.removeInviteError}
      />
    );
  }

  render() {
    const { items, allowInvite, renderList } = this.props;

    const renderers = {
      [CHOOSER_STATE.LOADING]: () => null,
      [CHOOSER_STATE.LIST]: () => (
        <>
          {this.renderTitle(TITLE_ID.CHOOSE_COMPANY)}
          {renderList(items)}
          {allowInvite && this.renderInviteForm(TITLE_ID.INVITE_BESIDE_CHOICES)}
        </>
      ),
      [CHOOSER_STATE.INVITE_ONLY]: () => (
        <>
          {this.renderTitle(TITLE_ID.INVITE_ALONE)}
          {this.renderInviteForm(null)}
        </>
      ),
      [CHOOSER_STATE.UNAVAILABLE]: () => (
        // Headless, so the message stands as the whole of the screen - it carries the
        // top margin the headline used to hold the content off the sheet's edge with.
        <div className="text-center mt-40 mb-35">
          <FormattedMessage
            id="only.sso.register.unavailable"
            defaultMessage="Registration is not available for this service at the moment."
          />
        </div>
      )
    };

    return (
      <div className="chooser-column">
        {renderers[getChooserState(items, allowInvite)]()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    allowInvite: keycloakRegistrationFlagsSelector(state).allowRegisterWithInvitationCode
  };
}

const mapDispatchToProps = { resolveInviteCompany, removeInviteError };

export default connect(mapStateToProps, mapDispatchToProps)(CompanyChooser);
